import {
  Box,
  Button,
  Card,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import { backend, CellValueUnion, gqlCellValueFromValue, LensHit } from "Backend";
import { Cell, CellKey, DistinctMode, UpsertFullValueGroupsDocument, ValueGroupToAddInput } from "generated/graphql";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import SkeletonRow from "components/SkeletonRow";
import HitTableRow from "./HitTableRow";
import TablePaginationActions from "./HitTablePagination";
import { LensHitFilterState } from "../LensHitFilterState";
import { StateHandle } from "StateHandle";
import { PushPin } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import produce from "immer";
import { HitTableContext } from "contexts/HitTableContext";
import { useAppSelector } from "redux/hooks";
import { selectFormId } from "redux/slices/form.slice";
import { CaseFormContext } from "contexts/CaseFormContext";
import HitTableInputRow from "./HitTableInputRow";
import { flatten } from "helpers/coll";
import { runGqlMutation } from "gqlClient";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";

const HITS_PER_CALL = 20;
const DOCUMENT_COLUMN_WIDTH = 65;
const DOCUMNENT_PAGE_WIDTH = 40;

function isDefaultDrugTableColumn(c: Cell): boolean {
  return (
    c.key === CellKey.Name ||
    c.key === CellKey.PrimaryDate ||
    c.key === CellKey.Dosage ||
    c.key === CellKey.RecordType ||
    c.key === CellKey.MedQuantity
  );
}

function findDefaultDrugTableColumns(cells: Cell[]): Cell[] {
  return cells.filter(isDefaultDrugTableColumn);
}

export interface HitTableProps {
  overrideHitHandle: StateHandle<LensHit | undefined>;
  lensHitFilterState: LensHitFilterState;
  selectedCategoryRuleId: string | undefined;
  setPageNum: (page: number) => void;
}

const HitTable: FC<HitTableProps> = ({ overrideHitHandle, lensHitFilterState, selectedCategoryRuleId, setPageNum }) => {
  const { searchParams, updateParam } = useSearchParamsWithUpdate();
  const inBulkEdit = searchParams.get("hideDoc") === "true";
  const {
    lensId,
    caseId,
    formField,
    formCardMap,
    setFormCardMap,
    setHitInEdit,
    hits,
    setHits,
    hitsFullCount,
    setHitsFullCount,
    checkedHits,
    setCheckedHits,
  } = useContext(HitTableContext);
  const defaultDrugTableColumns = useMemo(() => findDefaultDrugTableColumns(formField.cells), [formField.cells]);
  const [overrideHit, setOverrideHit] = overrideHitHandle;
  const [saving, setSaving] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const { enqueueSnackbar } = useSnackbar();
  const canPinAll = Array.from(formCardMap.values()).every((v) => v.canSave);
  const formId = useAppSelector(selectFormId);
  const caseForm = useContext(CaseFormContext);
  const pageHits = useMemo(() => {
    const start = page * HITS_PER_CALL;
    return hits.slice(start, start + HITS_PER_CALL);
  }, [hits, page]);
  const checkableHits = useMemo(() => pageHits.filter((h) => h?.hitId && !h.blockInForm), [pageHits]);
  const checkedHitsOnPage = useMemo(
    () => checkedHits.filter((c) => pageHits.find((pH) => pH.hitId === c.hitId)),
    [checkedHits, pageHits]
  );
  const allRowsChecked = checkedHitsOnPage.length === checkableHits.length;
  const allRowsIndeterminate = checkedHitsOnPage.length ? checkedHitsOnPage.length !== checkableHits.length : false;

  const saveAllPins = async () => {
    if (formId === undefined) return;
    const hitIds = Array.from(formCardMap.keys());
    setSaving(true);

    try {
      const valueGroups = await Promise.all(
        Array.from(formCardMap.entries()).map(async ([hitId, formCard]) => {
          const lensHit = hits.find((i) => i.hitId === hitId);
          const extraReferenceIndices =
            lensHit !== undefined && lensHit.collapsedHits.length > 0
              ? lensHit.collapsedHits.map((ch) => ch.blockIndex)
              : [];
          return formCard.getValueGroupToAdd(extraReferenceIndices, true);
        })
      );
      await runGqlMutation(UpsertFullValueGroupsDocument, {
        formId,
        caseId,
        fieldId: formField.id,
        yesNoValue: true,
        valueGroups: flatten<ValueGroupToAddInput>(valueGroups),
      });
      await caseForm.resetAllFieldsServer();
      setHits(
        produce<LensHit[]>((hitsDraft) => {
          hitIds.forEach((hitId) => {
            const hit = hitsDraft.find((h) => h.hitId === hitId);

            if (hit) {
              hit.blockInForm = true;
            }
          });
        })
      );

      setFormCardMap(new Map());
      setCheckedHits([]);
      enqueueSnackbar("Successfully saved profile data", {
        variant: "success",
      });
    } catch (e) {
      enqueueSnackbar("Unable to save profile data", {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const getHits = (body: { offset: number; limit?: number; hideLoader?: boolean }) => {
    const { offset, limit, hideLoader } = body;
    if (!hideLoader) {
      setLoading(true);
    }
    const rulesToUse =
      lensHitFilterState.reviewType === "grouped" && selectedCategoryRuleId
        ? [selectedCategoryRuleId]
        : lensHitFilterState.selectedRuleIds;
    backend
      .getLensRun({
        lensId,
        caseId,
        offset,
        limit: limit !== undefined ? limit : HITS_PER_CALL,
        includeSurroundings: false,
        filters: {
          ...lensHitFilterState.currentFilters,
          ruleIds: rulesToUse,
          distinctMode: DistinctMode.CategoryPage,
        },
      })
      .then((result) => {
        setLoading(false);
        setHits(
          produce((draft) => {
            draft.push(...result.hits);
          })
        );
        setHitsFullCount(result.fullCount);
      });
  };

  const onPageChange = (_event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    setPage(page);
    const end = Math.min(page * HITS_PER_CALL + HITS_PER_CALL, hitsFullCount);
    if (hits.length < end) {
      getHits({ offset: page * HITS_PER_CALL });
    }
  };

  const checkRow = (hit: LensHit, checked: boolean) => {
    setCheckedHits((checkedHits) => {
      if (checked) {
        return [...checkedHits, hit];
      } else {
        const foundIndex = checkedHits.findIndex((h) => h.hitId === hit.hitId);

        if (foundIndex > -1) {
          checkedHits.splice(foundIndex, 1);
        }
        return [...checkedHits];
      }
    });
  };

  const checkAllRows = () => {
    if (allRowsChecked) {
      setCheckedHits(produce((draft) => draft.filter((hit) => !checkableHits.find((c) => c.hitId === hit.hitId))));
    } else {
      setCheckedHits((prev) => [...new Set([...prev, ...checkableHits])]);
    }
  };

  const viewFinding = (hit: LensHit) => {
    if (inBulkEdit) return;
    setOverrideHit(hit);
    setPageNum(hit.block.originalPageNumber);
  };

  const toggleBulkEditMode = () => {
    if (inBulkEdit) {
      updateParam("hideDoc", null);
      setHitInEdit(undefined);
    } else {
      updateParam("hideDoc", "true");
    }
  };

  const applyBulkUpdate = (cell: Cell, value: CellValueUnion) => {
    Array.from(formCardMap.values()).forEach((formCard) => {
      const cellIndex = formCard.formGroupValuesState[0].findIndex((c) => c.cellId === cell.id);
      formCard.formGroupValuesState[1](
        produce((draft) => {
          const cellValue = draft[cellIndex];
          if (cellValue !== undefined) {
            cellValue.value = gqlCellValueFromValue(cell.type, value);
          }
        })
      );
    });
  };

  const onHitIgnored = (hit: LensHit) => {
    setHits(
      produce<LensHit[]>((draft) => {
        const foundIndex = draft.findIndex((h) => h.hitId === hit.hitId);

        if (foundIndex > -1) {
          draft.splice(foundIndex, 1);
        }
      })
    );
    checkRow(hit, false);
    setHitsFullCount((prevTotal) => prevTotal - 1);
    enqueueSnackbar("Finding Dismissed", {
      variant: "success",
    });
    getHits({ offset: HITS_PER_CALL - 1, limit: 1, hideLoader: true });
  };

  useEffect(() => {
    getHits({ offset: 0 });
  }, []);

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0, overflowX: "hidden" }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            "th, td": {
              padding: "0px 4px",
            },
          }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }} rowSpan={inBulkEdit ? 2 : 1} data-pendo="drug-table-check-all">
                <Checkbox checked={allRowsChecked} indeterminate={allRowsIndeterminate} onChange={checkAllRows} />
              </TableCell>
              <TableCell sx={{ width: DOCUMENT_COLUMN_WIDTH }} rowSpan={inBulkEdit ? 2 : 1}>
                Document
              </TableCell>
              <TableCell sx={{ width: DOCUMNENT_PAGE_WIDTH }} rowSpan={inBulkEdit ? 2 : 1}>
                Page
              </TableCell>
              {(inBulkEdit ? formField.cells : defaultDrugTableColumns).map((c) => {
                return (
                  <TableCell
                    key={c.id}
                    sx={{
                      whiteSpace: "nowrap",
                      minWidth: c.type === "date" ? "140px" : 0,
                      borderBottom: inBulkEdit ? "none" : "",
                    }}>
                    {c.name}
                    {!c.optional ? "*" : ""}
                  </TableCell>
                );
              })}
            </TableRow>
            {inBulkEdit && (
              <HitTableInputRow
                numRowsChecked={checkedHits.length}
                applyBulkUpdate={applyBulkUpdate}
                cells={formField.cells}
              />
            )}
          </TableHead>
          <TableBody>
            {pageHits.length && !loading
              ? (inBulkEdit ? checkedHits : pageHits).map((i) => {
                  if (i.hitId !== undefined) {
                    return (
                      <HitTableRow
                        onHitIgnored={onHitIgnored}
                        defaultDrugTableColumns={defaultDrugTableColumns}
                        isActive={i.hitId === overrideHit?.hitId}
                        onViewFinding={() => viewFinding(i)}
                        key={i.hitId}
                        hit={i}
                        checked={checkedHits.find((h) => h.hitId === i?.hitId) !== undefined}
                        setChecked={checkRow}
                        inBulkEdit={inBulkEdit}
                      />
                    );
                  } else {
                    return <></>;
                  }
                })
              : [0, 1, 2].map((i) => <SkeletonRow key={i} columnNum={defaultDrugTableColumns.length + 3} />)}
          </TableBody>
        </Table>
      </TableContainer>
      <Card
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingY: 2,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          paddingRight: 3,
        }}>
        {!inBulkEdit && (
          <TablePagination
            sx={{
              ".MuiTablePagination-spacer": {
                flex: 0,
              },
              borderBottom: 0,
              padding: 0,
            }}
            rowsPerPageOptions={[]}
            rowsPerPage={HITS_PER_CALL}
            colSpan={formField.cells.length + 1}
            count={hitsFullCount}
            page={page}
            onPageChange={onPageChange}
            ActionsComponent={TablePaginationActions}
          />
        )}
        {inBulkEdit ? (
          <Box sx={{ display: "flex", justifyContent: "center", flexGrow: 1, alignItem: "center" }}>
            <Button color="primary" onClick={toggleBulkEditMode} sx={{ mr: 2 }} size="small">
              Cancel
            </Button>
            <Tooltip
              arrow
              title={checkedHits.length && !canPinAll && !saving ? "Some rows are missing required information" : ""}>
              <span>
                <Button
                  data-pendo="drug-table-save"
                  color="primary"
                  variant="contained"
                  size="small"
                  disabled={!checkedHits.length || !canPinAll}
                  onClick={saveAllPins}
                  startIcon={<PushPin />}>
                  Save {checkedHits.length ? checkedHits.length : ""}
                </Button>
              </span>
            </Tooltip>
          </Box>
        ) : (
          <Button
            color="primary"
            variant="contained"
            size="small"
            data-pendo="drug-table-pin"
            disabled={checkedHits.length === 0}
            onClick={toggleBulkEditMode}
            startIcon={<PushPin />}>
            Pin {checkedHits.length ? checkedHits.length : ""}
          </Button>
        )}
      </Card>
    </>
  );
};

export default HitTable;
